import React, { createContext, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import API_URL from '../api/baseURL';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('authToken') || "");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch full user profile by id
  const fetchUserProfile = async (userId, token) => {
    try {
      const res = await fetch(`${API_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch user profile');
      const data = await res.json();
      return data;
    } catch (err) {
      console.error('Fetch user profile error:', err);
      return null;
    }
  };

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (!decoded?.id || !decoded?.role) throw new Error('Invalid token payload');
      // Always fetch fresh user data from backend (with details)
      fetchUserProfile(decoded.id, token).then(profile => {
        if (profile) {
          setUser({ ...profile, role: profile.role?.toLowerCase() });
        } else {
          setUser({
            _id: decoded.id,
            role: decoded.role?.toLowerCase() || null,
            ...decoded,
          });
        }
        setLoading(false);
      });
    } catch (err) {
      console.error('Token decode failed:', err);
      logout();
      setLoading(false);
    }
  }, [token]);

  const login = (newToken) => {
    localStorage.setItem('authToken', newToken);
    setToken(newToken);
    try {
      const decoded = jwtDecode(newToken);
      if (!decoded?.id || !decoded?.role) throw new Error('Invalid token payload');
      // Fetch profile on login
      fetchUserProfile(decoded.id, newToken).then(profile => {
        if (profile) {
          setUser({ ...profile, role: profile.role?.toLowerCase() });
        } else {
          setUser({
            _id: decoded.id,
            role: decoded.role?.toLowerCase() || null,
            ...decoded,
          });
        }
        setLoading(false);
      });
    } catch (err) {
      console.error('Token decode failed at login:', err);
      logout();
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setToken("");
    setUser(null);
    setLoading(false);
  };

  const getRole = () => user?.role;

  return (
    <AuthContext.Provider value={{ token, user, login, logout, getRole, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
